/**
 * Research citation Notes for hardcoded workbook cells.
 *
 * `set_citations` is the native Excel agent's citation tool. For each exact
 * worksheet cell, the model copies one complete metadata record from a
 * FactSet AI response. This module validates the cell and metadata, then
 * writes the standardized yellow source Note. No citation-ID lookup or
 * run-scoped metadata map participates in this path.
 *
 * A batch is intentionally partial. Cells that are blank, formula-backed,
 * duplicated, unresolved, or already carrying a user-authored Note are
 * reported individually, while every valid cell shares one workbook apply.
 */

import type { ChartCapabilityToolDef } from "./chartCapabilityTools";
import {
  buildSourceNoteAudit,
  citationCellKey,
  formatCitationSourceNote,
  isGeneratedSourceNote,
  normalizeNoteText,
  type CitationSourceMetadata,
  type CitationSourceType,
  type CitationNoteTarget,
} from "./citationNotes";
import { parseSingleCellAddress } from "./excelCellAddress";
import { getNote } from "./excelHostOps";
import { readRangeContent } from "./excelService";
import type { ExecutorToolRuntimeContext } from "./executorToolRuntime";
import {
  applyValidatedWorkbookModification,
  WORKBOOK_EDIT_STEPS_REQUIRED_ERROR,
} from "./executorWriteTools";
import { isAbortError } from "../shared/utils/abortError";

const sheetNameProperty = {
  type: "string",
  minLength: 1,
  description: "Worksheet name observed from list_sheets.",
};

const addressProperty = {
  type: "string",
  minLength: 1,
  description:
    "One worksheet-local A1 cell address, such as F12. Ranges and sheet-qualified addresses are invalid.",
};

const nullableMetadataString = (description: string) => ({
  type: ["string", "null"],
  description,
});

const citationEntrySchema = {
  type: "object",
  properties: {
    sheetName: sheetNameProperty,
    address: addressProperty,
    type: {
      type: "string",
      enum: ["document", "web", "factset_data"],
      description: "Exact source type copied from the FactSet AI citation metadata.",
    },
    source: {
      type: "string",
      minLength: 1,
      description: "Exact source label copied from FactSet AI.",
    },
    document: nullableMetadataString("Document, page, dataset, or transcript title."),
    category: nullableMetadataString("Document or dataset category."),
    company: nullableMetadataString("Company or publisher name."),
    published_date: nullableMetadataString("Publication date exactly as returned."),
    url: nullableMetadataString("Durable source URL exactly as returned."),
    ticker: nullableMetadataString("FactSet identifier or ticker."),
    field: nullableMetadataString("FactSet field or cited table column."),
    period: nullableMetadataString("Fiscal or reporting period."),
    as_of_date: nullableMetadataString("Market-data or estimate as-of date."),
    units: nullableMetadataString("Units associated with the cited value."),
    fql_formula: nullableMetadataString("FactSet audit formula when supplied."),
  },
  required: [
    "sheetName",
    "address",
    "type",
    "source",
    "document",
    "category",
    "company",
    "published_date",
    "url",
    "ticker",
    "field",
    "period",
    "as_of_date",
    "units",
    "fql_formula",
  ],
  additionalProperties: false,
};

/**
 * Model-facing tool that attaches research provenance to existing hardcoded
 * cells. The model supplies one source's metadata and the runtime creates the Excel Note.
 */
export const SET_CITATIONS_TOOL: ChartCapabilityToolDef = {
  name: "set_citations",
  description:
    "Attach FactSet AI source Notes to existing hardcoded cells that contain externally retrieved facts, values, identities, or claims. " +
    "Pass one entry per cell and copy exactly one complete metadata record from the relevant factset_ai_query response. " +
    "The metadata fields are top-level; do not pass a citation ID and never infer, edit, or fabricate metadata. " +
    "Batch every applicable cell for the current write into one call. Generic headers, labels, and units are not citation targets when they are presentation structure, but they are eligible when their content is itself retrieved information. " +
    "Do not supply Note text, and do not use this tool for formulas, blank cells, " +
    "or cells that do not have one directly supporting FactSet AI source.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      citations: {
        type: "array",
        minItems: 1,
        description: "Hardcoded cells and one exact FactSet AI source metadata record per cell.",
        items: citationEntrySchema,
      },
    },
    required: ["citations"],
    additionalProperties: false,
  },
};

/** Stable machine-readable reason a citation request was not completed. */
export type SetCitationFailureCode =
  | "INVALID_INPUT"
  | "RUN_PLAN_REQUIRED"
  | "NOTE_HOST_UNSUPPORTED"
  | "NOTE_ATTACHMENT_DISABLED"
  | "DUPLICATE_TARGET"
  | "FORMULA_STATE_UNAVAILABLE"
  | "FORMULA_CELL"
  | "BLANK_CELL"
  | "CELL_READ_FAILED"
  | "EXISTING_NOTE_READ_FAILED"
  | "USER_NOTE_PRESERVED"
  | "NOTE_APPLY_RESULT_UNREADABLE"
  | "NOTE_APPLY_REJECTED"
  | "NOTE_READBACK_MISMATCH"
  | "NOTE_READBACK_FAILED"
  | "INTERNAL_RESULT_MISSING";

/** Safe structured context accompanying one citation failure. */
export type SetCitationDiagnostics = Record<string, unknown>;

/** Structured failure shared by batch, cell, apply, and verification paths. */
export interface SetCitationFailure {
  code: SetCitationFailureCode;
  error: string;
  diagnostics?: SetCitationDiagnostics;
}

/** One cell's outcome inside a `set_citations` batch. */
export interface SetCitationCellResult {
  sheetName: string;
  address: string;
  status: "cited" | "failed";
  code?: SetCitationFailureCode;
  error?: string;
  diagnostics?: SetCitationDiagnostics;
}

/** Model-facing result for one `set_citations` call. */
export interface SetCitationsResult {
  ok: boolean;
  applied: boolean;
  citedCount: number;
  failedCount: number;
  results: SetCitationCellResult[];
  code?: SetCitationFailureCode;
  error?: string;
  diagnostics?: SetCitationDiagnostics;
}

/** One normalized cell and its one agent-selected FactSet AI source. */
interface CitationRequest extends CitationSourceMetadata {
  sheetName: string;
  address: string;
}

/** A cell whose source Note is ready for the shared workbook apply. */
interface PreparedCitation {
  request: CitationRequest;
  target: CitationNoteTarget;
  replaceExisting: boolean;
}

/**
 * Cite one batch of hardcoded cells and return an independent result for each.
 *
 * Valid cells are applied together. A failure on one cell does not discard a
 * successful Note on another cell. Caller cancellation propagates unchanged.
 *
 * @param ctx - Run context containing the plan and host apply callback.
 * @param rawInput - Model arguments containing one cell and one source metadata record per entry.
 * @returns Serialized `{ ok, applied, citedCount, failedCount, results }`.
 */
export async function executeSetCitationsTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
): Promise<string> {
  const requests = normalizeCitationRequests(rawInput);
  if (requests.error) {
    return JSON.stringify(failedBatch({
      code: "INVALID_INPUT",
      error: requests.error,
      diagnostics: { issues: requests.issues },
    }));
  }
  if (ctx.planState.steps.length === 0) {
    return JSON.stringify(failedBatch({
      code: "RUN_PLAN_REQUIRED",
      error: WORKBOOK_EDIT_STEPS_REQUIRED_ERROR,
      diagnostics: { issues: [WORKBOOK_EDIT_STEPS_REQUIRED_ERROR] },
    }));
  }

  const hostLimitation = citationHostLimitation(ctx);
  if (hostLimitation) {
    return JSON.stringify(failedBatch(hostLimitation));
  }

  const results = new Map<number, SetCitationCellResult>();
  const uniqueRequests = rejectDuplicateRequests(requests.requests, results);
  const prepared: PreparedCitation[] = [];
  for (const request of uniqueRequests) {
    const preparation = await prepareCitation(ctx, request.request);
    if ("failure" in preparation) {
      results.set(request.index, failedCell(request.request, preparation.failure));
      continue;
    }
    prepared.push(preparation.prepared);
  }

  if (prepared.length > 0) {
    const applyError = await applyCitationNotes(ctx, prepared);
    if (applyError) {
      for (const citation of prepared) {
        results.set(
          requestIndex(requests.requests, citation.request),
          failedCell(citation.request, applyError),
        );
      }
    } else {
      await verifyAppliedCitations(ctx, prepared, requests.requests, results);
    }
  }

  return JSON.stringify(assembleResult(requests.requests, results));
}

/**
 * Build a batch-level failure returned before any cell is processed.
 *
 * @param failure - Structured reason the call cannot start.
 * @returns A result with no per-cell entries.
 */
function failedBatch(failure: SetCitationFailure): SetCitationsResult {
  return {
    ok: false,
    applied: false,
    citedCount: 0,
    failedCount: 0,
    results: [],
    code: failure.code,
    error: failure.error,
    ...(failure.diagnostics ? { diagnostics: failure.diagnostics } : {}),
  };
}

/**
 * Build the failure record for one cell.
 *
 * @param request - Normalized cell the call attempted to cite.
 * @param failure - Structured reason that cell was not cited.
 * @returns A failed cell result.
 */
function failedCell(
  request: CitationRequest,
  failure: SetCitationFailure,
): SetCitationCellResult {
  return {
    sheetName: request.sheetName,
    address: request.address,
    status: "failed",
    code: failure.code,
    error: failure.error,
    ...(failure.diagnostics ? { diagnostics: failure.diagnostics } : {}),
  };
}

/**
 * Reject the call when this run cannot persist citation Notes.
 *
 * @param ctx - Run context carrying the host Note capability.
 * @returns A structured host limitation, or `undefined` when Notes can be written.
 */
function citationHostLimitation(
  ctx: ExecutorToolRuntimeContext,
): SetCitationFailure | undefined {
  if (ctx.session.citationNoteCapability === "unsupported") {
    const error = "This Excel host cannot write citation Notes.";
    return {
      code: "NOTE_HOST_UNSUPPORTED",
      error,
      diagnostics: { issues: [error] },
    };
  }
  if (ctx.session.citationNoteAttachmentDisabled) {
    const error =
      "Citation Notes are unavailable for the rest of this run because an earlier Note write could not be verified.";
    return {
      code: "NOTE_ATTACHMENT_DISABLED",
      error,
      diagnostics: {
        issues: [error],
      },
    };
  }
  return undefined;
}

/**
 * Parse model arguments into one normalized request per cell.
 *
 * A malformed entry fails the whole batch so the model can correct the call.
 * A valid address is rewritten to a worksheet-local A1 cell, such as `$F$12`
 * becoming `F12`.
 *
 * @param rawInput - Raw `set_citations` arguments.
 * @returns Normalized requests, or a batch error when the payload is invalid.
 */
function normalizeCitationRequests(
  rawInput: unknown,
): { requests: CitationRequest[]; error?: string; issues: string[] } {
  if (!rawInput || typeof rawInput !== "object" || Array.isArray(rawInput)) {
    const error = "set_citations requires a citations array.";
    return { requests: [], error, issues: [error] };
  }
  const citations = (rawInput as { citations?: unknown }).citations;
  if (!Array.isArray(citations) || citations.length === 0) {
    const error = "set_citations requires at least one citation entry.";
    return { requests: [], error, issues: [error] };
  }

  const requests: CitationRequest[] = [];
  const issues: string[] = [];
  for (let index = 0; index < citations.length; index += 1) {
    const entry = citations[index];
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
      issues.push(`citations[${index}] must be an object.`);
      continue;
    }
    const record = entry as Record<string, unknown>;
    const sheetName = typeof record.sheetName === "string" ? record.sheetName.trim() : "";
    const address = typeof record.address === "string" ? record.address.trim() : "";
    const sourceType = typeof record.type === "string" ? record.type.trim() : "";
    const source = typeof record.source === "string" ? record.source.trim() : "";
    let valid = true;
    if (!sheetName) {
      issues.push(`citations[${index}].sheetName is required.`);
      valid = false;
    }
    if (!address) {
      issues.push(`citations[${index}].address is required.`);
      valid = false;
    } else if (address.includes("!")) {
      issues.push(
        `citations[${index}].address must be a worksheet-local cell. Put the sheet in sheetName.`,
      );
      valid = false;
    } else if (!parseSingleCellAddress(address)) {
      issues.push(
        `citations[${index}].address must be one A1 cell, not a range or malformed address.`,
      );
      valid = false;
    }
    if (!["document", "web", "factset_data"].includes(sourceType)) {
      issues.push(
        `citations[${index}].type must be document, web, or factset_data.`,
      );
      valid = false;
    }
    if (!source) {
      issues.push(`citations[${index}].source is required.`);
      valid = false;
    }
    const metadataFields = [
      "document",
      "category",
      "company",
      "published_date",
      "url",
      "ticker",
      "field",
      "period",
      "as_of_date",
      "units",
      "fql_formula",
    ] as const;
    const metadata: Partial<CitationSourceMetadata> = {};
    for (const field of metadataFields) {
      const value = record[field];
      if (value !== null && typeof value !== "string") {
        issues.push(`citations[${index}].${field} must be a string or null.`);
        valid = false;
        continue;
      }
      const normalized = typeof value === "string" ? value.trim() : "";
      if (normalized.length > 2_000) {
        issues.push(`citations[${index}].${field} is too long.`);
        valid = false;
      } else if (normalized) {
        metadata[field] = normalized;
      }
    }
    if (typeof metadata.url === "string") {
      try {
        const parsed = new URL(metadata.url);
        if (
          !["http:", "https:"].includes(parsed.protocol) ||
          parsed.username ||
          parsed.password
        ) {
          throw new Error("unsafe URL");
        }
      } catch {
        issues.push(`citations[${index}].url must be a safe HTTP(S) URL or null.`);
        valid = false;
      }
    }
    if (!valid) continue;
    const parsedAddress = parseSingleCellAddress(address)!;
    requests.push({
      sheetName,
      address: parsedAddress.normalizedAddress,
      type: sourceType as CitationSourceType,
      source,
      ...metadata,
    });
  }
  return {
    requests,
    ...(issues.length > 0 ? { error: issues[0] } : {}),
    issues,
  };
}

/**
 * Fail every entry that names the same worksheet cell more than once.
 *
 * Unique cells continue. Each duplicate is recorded in `results`.
 *
 * @param requests - Normalized citation requests in call order.
 * @param results - Per-cell outcomes, keyed by the request index.
 * @returns The requests that name a cell exactly once.
 */
function rejectDuplicateRequests(
  requests: readonly CitationRequest[],
  results: Map<number, SetCitationCellResult>,
): Array<{ index: number; request: CitationRequest }> {
  const counts = new Map<string, number>();
  for (const request of requests) {
    const key = citationCellKey(request.sheetName, request.address).toLowerCase();
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const unique: Array<{ index: number; request: CitationRequest }> = [];
  requests.forEach((request, index) => {
    const key = citationCellKey(request.sheetName, request.address).toLowerCase();
    if ((counts.get(key) ?? 0) > 1) {
      results.set(index, failedCell(request, {
        code: "DUPLICATE_TARGET",
        error:
          `Duplicate citation target ${request.sheetName}!${request.address}. ` +
          "Send one entry for that cell.",
        diagnostics: {
          target: citationCellKey(request.sheetName, request.address),
          occurrences: counts.get(key),
        },
      }));
      return;
    }
    unique.push({ index, request });
  });
  return unique;
}

/**
 * Format one supplied source, validate its cell, and decide whether its Note
 * can be replaced. No citation ID lookup or provenance comparison occurs.
 *
 * @param ctx - Run context containing the pinned workbook.
 * @param request - Normalized cell and its one source metadata record.
 * @returns A prepared Note write, or a structured failure for only this cell.
 */
async function prepareCitation(
  ctx: ExecutorToolRuntimeContext,
  request: CitationRequest,
): Promise<{ prepared: PreparedCitation } | { failure: SetCitationFailure }> {
  const formatted = formatCitationSourceNote(request);

  const contentError = await assertHardcodedValue(ctx, request);
  if (contentError) return { failure: contentError };

  const existingNote = await readExistingNote(ctx, request);
  if ("failure" in existingNote) return { failure: existingNote.failure };
  if (existingNote.text.trim() && !isGeneratedSourceNote(existingNote.text)) {
    return {
      failure: {
        code: "USER_NOTE_PRESERVED",
        error: `Preserved the user-authored Note on ${request.sheetName}!${request.address}.`,
        diagnostics: { existingNoteLength: existingNote.text.length },
      },
    };
  }
  if (ctx.session.citationNoteCapability === "unknown") {
    ctx.session.citationNoteCapability = "supported";
  }

  return {
    prepared: {
      request,
      replaceExisting: existingNote.text.length > 0,
      target: {
        sheetName: request.sheetName,
        address: request.address,
        citationIds: [],
        noteText: formatted.noteText,
        richTextRuns: formatted.richTextRuns,
      },
    },
  };
}

/**
 * Read one cell and accept it only when it holds a non-empty hardcoded value.
 *
 * Excel echoes a hardcoded value in the formula slot, so only text that starts
 * with "=" is a formula. A blank or an unverifiable formula state rejects the
 * cell. Zero and false remain valid hardcoded values.
 *
 * @param ctx - Run context used to select the pinned workbook.
 * @param request - Normalized cell to inspect.
 * @returns A structured failure, or `undefined` when the cell can be cited.
 */
async function assertHardcodedValue(
  ctx: ExecutorToolRuntimeContext,
  request: CitationRequest,
): Promise<SetCitationFailure | undefined> {
  try {
    const observed = await readRangeContent(
      request.sheetName,
      request.address,
      { includeFormulas: true },
      ctx.session.pinnedWorkbookName,
    );
    if (observed.warnings?.includes("formulas_unavailable")) {
      return {
        code: "FORMULA_STATE_UNAVAILABLE",
        error:
          `Could not verify whether ${request.sheetName}!${request.address} contains a formula.`,
        diagnostics: { warnings: observed.warnings },
      };
    }
    const formula = observed.range?.formulas?.[0]?.[0];
    if (typeof formula === "string" && formula.trimStart().startsWith("=")) {
      return {
        code: "FORMULA_CELL",
        error:
          `${request.sheetName}!${request.address} contains a formula. ` +
          "Cite the hardcoded inputs instead.",
        diagnostics: { formulaLength: formula.length },
      };
    }
    const value = observed.range?.values?.[0]?.[0];
    if (
      observed.emptyRange ||
      !observed.range ||
      value === null ||
      value === undefined ||
      (typeof value === "string" && !value.trim())
    ) {
      return {
        code: "BLANK_CELL",
        error:
          `${request.sheetName}!${request.address} is blank. ` +
          "Write the hardcoded value before citing it.",
      };
    }
    return undefined;
  } catch (error) {
    if (isAbortError(error)) throw error;
    return {
      code: "CELL_READ_FAILED",
      error: `Could not read ${request.sheetName}!${request.address} before citing it.`,
      diagnostics: { cause: diagnosticCause(error) },
    };
  }
}

/**
 * Read the Note already attached to one cell.
 *
 * @param ctx - Run context used to select the pinned workbook.
 * @param request - Cell whose existing Note is inspected.
 * @returns The current Note text, or a structured failure when the host read fails.
 */
async function readExistingNote(
  ctx: ExecutorToolRuntimeContext,
  request: CitationRequest,
): Promise<{ text: string } | { failure: SetCitationFailure }> {
  try {
    const text = await getNote({
      address: request.address,
      referenceType: "absolute",
      worksheetName: request.sheetName,
      ...(ctx.session.pinnedWorkbookName
        ? { workbookName: ctx.session.pinnedWorkbookName }
        : {}),
    });
    return { text };
  } catch (error) {
    if (isAbortError(error)) throw error;
    return {
      failure: {
        code: "EXISTING_NOTE_READ_FAILED",
        error: `Could not read the existing Note on ${request.sheetName}!${request.address}.`,
        diagnostics: { cause: diagnosticCause(error) },
      },
    };
  }
}

/**
 * Convert an unknown caught value into a bounded diagnostic message.
 *
 * The returned text is intended for model-facing troubleshooting and excludes
 * stack traces. Long host messages are truncated to keep tool responses small.
 *
 * @param error - Unknown value caught from an Excel or tool boundary.
 * @returns A non-empty diagnostic cause of at most 500 characters.
 */
function diagnosticCause(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  const normalized = message.trim() || "Unknown host error";
  return normalized.length <= 500
    ? normalized
    : `${normalized.slice(0, 499).trimEnd()}…`;
}

/**
 * Write every prepared citation Note in one workbook apply.
 *
 * The Notes are ordinary note annotations, so the host keeps them. The active
 * plan step is supplied internally and is not a model argument.
 *
 * @param ctx - Run context containing the current step and host apply callback.
 * @param prepared - Cells whose Notes are ready to write.
 * @returns A structured failure when the apply does not succeed.
 */
async function applyCitationNotes(
  ctx: ExecutorToolRuntimeContext,
  prepared: readonly PreparedCitation[],
): Promise<SetCitationFailure | undefined> {
  const output = await applyValidatedWorkbookModification(ctx, {
    stepId: ctx.planState.currentStepId ?? null,
    targetSheet: prepared[0]?.request.sheetName ?? ctx.session.sessionLockSheet,
    modifications: {
      annotations: prepared.map((citation) => ({
        kind: "note" as const,
        action: "set" as const,
        sheetName: citation.target.sheetName,
        address: citation.target.address,
        text: citation.target.noteText,
        replaceExisting: citation.replaceExisting,
        richTextRuns: citation.target.richTextRuns,
      })),
      message: `Set ${prepared.length} citation ${prepared.length === 1 ? "Note" : "Notes"}`,
    },
  });
  return parseCitationApplyResult(output);
}

/**
 * Interpret the shared workbook apply acknowledgement for `set_citations`.
 *
 * This parser deliberately reports only response shape and bounded causes; it
 * does not echo the raw apply payload into the model transcript.
 *
 * @param output - Serialized acknowledgement returned by the shared write path.
 * @returns `undefined` for a successful apply, otherwise a structured failure.
 */
export function parseCitationApplyResult(
  output: string,
): SetCitationFailure | undefined {
  let parsed: { applied?: unknown; error?: unknown; message?: unknown };
  try {
    parsed = JSON.parse(output) as { applied?: unknown; error?: unknown; message?: unknown };
  } catch {
    return {
      code: "NOTE_APPLY_RESULT_UNREADABLE",
      error: "Citation Note apply returned an unreadable result.",
      diagnostics: { outputLength: output.length },
    };
  }
  if (parsed.applied === true) return undefined;
  const cause = typeof parsed.error === "string" && parsed.error.trim()
    ? parsed.error
    : typeof parsed.message === "string" && parsed.message.trim()
      ? parsed.message
      : "Citation Notes were not applied.";
  return {
    code: "NOTE_APPLY_REJECTED",
    error: cause,
    diagnostics:
      cause === "Citation Notes were not applied."
        ? { responseFields: Object.keys(parsed).sort() }
        : { cause },
  };
}

/**
 * Read each applied Note back and retain a host-application audit.
 *
 * A mismatch or an unreadable Note fails that cell.
 * Caller cancellation propagates unchanged.
 *
 * @param ctx - Run context used to read Notes and store provenance.
 * @param prepared - Notes that the apply reported as written.
 * @param requests - Original normalized requests, used to place each result.
 * @param results - Per-cell outcomes updated in place.
 */
async function verifyAppliedCitations(
  ctx: ExecutorToolRuntimeContext,
  prepared: readonly PreparedCitation[],
  requests: readonly CitationRequest[],
  results: Map<number, SetCitationCellResult>,
): Promise<void> {
  const actualNotes = new Map<string, string | undefined>();
  for (const citation of prepared) {
    const cell = citationCellKey(citation.target.sheetName, citation.target.address);
    const index = requestIndex(requests, citation.request);
    try {
      const actual = await getNote({
        address: citation.target.address,
        referenceType: "absolute",
        worksheetName: citation.target.sheetName,
        ...(ctx.session.pinnedWorkbookName
          ? { workbookName: ctx.session.pinnedWorkbookName }
          : {}),
      });
      actualNotes.set(cell, actual);
      const normalizedActual = normalizeNoteText(actual);
      const normalizedExpected = normalizeNoteText(citation.target.noteText);
      if (normalizedActual !== normalizedExpected) {
        results.set(index, failedCell(citation.request, {
          code: "NOTE_READBACK_MISMATCH",
          error:
            `Citation Note on ${citation.request.sheetName}!${citation.request.address} ` +
            "did not match after apply.",
          diagnostics: {
            expectedLength: normalizedExpected.length,
            actualLength: normalizedActual.length,
          },
        }));
        continue;
      }
      results.set(index, {
        sheetName: citation.request.sheetName,
        address: citation.request.address,
        status: "cited",
      });
    } catch (error) {
      if (isAbortError(error)) throw error;
      actualNotes.set(cell, undefined);
      results.set(index, failedCell(citation.request, {
        code: "NOTE_READBACK_FAILED",
        error:
          `Could not verify the citation Note on ` +
          `${citation.request.sheetName}!${citation.request.address}.`,
        diagnostics: { cause: diagnosticCause(error) },
      }));
    }
  }

  const audit = buildSourceNoteAudit(
    prepared.map((citation) => citation.target),
    actualNotes,
  );
  ctx.accumulators.sourceNoteAudits ??= [];
  ctx.accumulators.sourceNoteAudits.push(audit);
}

/**
 * Find one request object inside the normalized batch.
 *
 * @param requests - Normalized requests in call order.
 * @param request - The same request object returned by normalization.
 * @returns The request index, or `-1` when the object is absent.
 */
function requestIndex(
  requests: readonly CitationRequest[],
  request: CitationRequest,
): number {
  return requests.findIndex((candidate) => candidate === request);
}

/**
 * Build the model-facing result in the original entry order.
 *
 * `ok` is true only when every cell was cited. `applied` is true when at least
 * one Note was verified.
 *
 * @param requests - Normalized requests in call order.
 * @param results - Per-cell outcomes keyed by request index.
 * @returns The serialized result body for the tool call.
 */
function assembleResult(
  requests: readonly CitationRequest[],
  results: Map<number, SetCitationCellResult>,
): SetCitationsResult {
  const ordered = requests.map((request, index) =>
    results.get(index) ?? failedCell(request, {
      code: "INTERNAL_RESULT_MISSING",
      error: "Citation result was not recorded.",
      diagnostics: { requestIndex: index },
    }),
  );
  const citedCount = ordered.filter((result) => result.status === "cited").length;
  const failedCount = ordered.length - citedCount;
  return {
    ok: failedCount === 0,
    applied: citedCount > 0,
    citedCount,
    failedCount,
    results: ordered,
  };
}
